import { Component } from '@theme/component';

/**
 * Auto-shows the site-wide duty & import notice (sections/duty-notice.liquid)
 * once per visitor, then remembers the dismissal so it doesn't nag them on
 * every page load. Lives *inside* the shared <dialog-component>'s <dialog>
 * (which owns open/close, scroll-lock, outside-click, and animation) rather
 * than wrapping it, so <dialog-component> can still resolve its own
 * `ref="dialog"` — nesting a second Component around that ref would hide it
 * from the outer one's ref lookup.
 *
 * "Once per visitor" is versioned: bumping the `notice-version` attribute
 * (driven by the section's "Notice version" setting) clears everyone's
 * dismissal, for whenever the wording changes enough that people should see
 * it again.
 */
export class DutyNotice extends Component {
  connectedCallback() {
    super.connectedCallback();

    const dialogComponent = this.closest('dialog-component');
    if (!dialogComponent) return;

    dialogComponent.addEventListener('dialog:close', () => {
      try {
        localStorage.setItem(this.#storageKey, '1');
      } catch {
        // Storage unavailable (private browsing, blocked, etc.) — the
        // notice will simply show again next visit, which is an
        // acceptable fallback for a non-critical convenience.
      }
    });

    if (this.getAttribute('auto-show') !== 'true') return;

    let alreadyDismissed = false;
    try {
      alreadyDismissed = localStorage.getItem(this.#storageKey) === '1';
    } catch {
      alreadyDismissed = false;
    }

    if (alreadyDismissed) return;

    // Let the visitor start browsing before taking over focus with a modal.
    const delaySeconds = Number(this.getAttribute('delay-seconds'));
    const delayMs = Number.isFinite(delaySeconds) && delaySeconds > 0 ? delaySeconds * 1000 : 0;
    setTimeout(() => {
      /** @type {any} */ (dialogComponent).showDialog();
    }, delayMs);
  }

  get #storageKey() {
    return `aniezeDutyNoticeDismissed:${this.getAttribute('notice-version') || '1'}`;
  }
}

if (!customElements.get('duty-notice')) {
  customElements.define('duty-notice', DutyNotice);
}
